///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Nearest-city reverse geocode against the local locations DB, for
/// naming dropped pins. Uses the distance-ordered queryNearest (NOT the
/// rank-ordered queryByBBox, whose limit truncation can drop a tiny town
/// right at the pin in favor of bigger cities across the box), bounded
/// by a distance cap: a pin in the open ocean should get coordinates,
/// not a city 800 km away.
///

import { LocationsTable } from "@/earthbrowser/dataset-distribution/locations/LocationsTable"
import { haversineKm } from "@/utils/savedViews"

const cLookupRadiusDeg = 1
const cMaxDistanceKm = 50

/** Nearest city name within cMaxDistanceKm, or null (open ocean, DB unavailable) */
export async function nearestPlaceName(lat: number, lon: number): Promise<string | null> {
	try {
		const rows = await LocationsTable.queryNearest(lat, lon, cLookupRadiusDeg, 1)
		const row = rows[0]
		if (row && haversineKm(lat, lon, row.lat, row.lon) <= cMaxDistanceKm) {
			return row.name
		}
		return null
	} catch {
		return null
	}
}

/** Compact coordinate label ("45.52°N, 122.68°W") for unnamed pins */
export function coordsLabel(lat: number, lon: number): string {
	const latLabel = `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}`
	const lonLabel = `${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`
	return `${latLabel}, ${lonLabel}`
}
